// Utilitários do módulo Operacional → Contratos.
// Extraído de OperacionalContratos.tsx no refactor de 2026-05-23.

import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import React from 'react';
import { API_URL } from '../../config';
import { fmtDataBr as _fmtDataBr, hojeIso } from '../../utils/dataFmt';
import type {
  Contrato, UsuarioOp, ClienteSlim, ContatoClienteSlim, CatAtividadeSlim, OpcoesArt, StatusArtVigencia,
} from './types';

export async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

export const fmt = (v: number | string | null | undefined) => {
  const n = typeof v === 'number' ? v : parseFloat(v as any);
  if (isNaN(n)) return 'R$ 0';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
};

// Helper essencial: Postgres retorna numeric como string em node-pg, então
// "347000.00" + "13300.00" vira "347000.0013300.00" (concatenação) ao invés
// de soma. Sempre usar num() antes de operação aritmética.
export const num = (v: number | string | null | undefined): number => {
  if (v == null) return 0;
  const n = typeof v === 'number' ? v : parseFloat(v as any);
  return isNaN(n) ? 0 : n;
};

export const fmtData = _fmtDataBr;

// Calcula prazo em dias (data_termino_previsto - data_inicio_prevista). Top-level
// pra ser acessível tanto em SecaoProjetos quanto em ListaContratos (cmp).
export function prazoDias(c: Contrato): number | null {
  if (!c.data_inicio_prevista || !c.data_termino_previsto) return null;
  const ini = new Date(String(c.data_inicio_prevista).slice(0, 10) + 'T12:00:00').getTime();
  const fim = new Date(String(c.data_termino_previsto).slice(0, 10) + 'T12:00:00').getTime();
  if (isNaN(ini) || isNaN(fim)) return null;
  return Math.round((fim - ini) / 86400000);
}

export function fmtTamanho(b: number | null): string {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function iconeArquivo(tipo: string | null): string {
  switch (tipo) {
    case 'pdf':     return '📄';
    case 'image':   return '🖼️';
    case 'word':    return '📝';
    case 'sheet':   return '📊';
    case 'slide':   return '📊';
    case 'kml': case 'kmz': return '🗺️';
    case 'cad':     return '📐';
    case 'archive': return '🗜️';
    default:        return '📎';
  }
}

// Status visual auxiliar — pra ARTs sem status_crea preenchido, deduz vigência
export function statusArtVigencia(a: { inicio_art: string|null; fim_art: string|null }): StatusArtVigencia {
  const hoje = hojeIso();
  if (a.fim_art && a.fim_art < hoje) return 'vencida';
  if (a.inicio_art && a.inicio_art > hoje) return 'futura';
  if (a.inicio_art || a.fim_art) return 'vigente';
  return 'sem_dados';
}

export function btnAtalho(_atual: string): React.CSSProperties {
  return {
    padding: '5px 12px', fontSize: 11, background: '#fff',
    border: '0.5px solid #ddd', color: '#555', borderRadius: 3, cursor: 'pointer',
  };
}

// Helper p/ ModalReorganizarParcelas — soma `prazo` dias ao `emissao`.
// Versão original (do arquivo principal) — valida que a data é real (não 31/06).
export function _calcVencFromEmissao(emissao: string, prazo: number): string {
  if (!emissao || !/^\d{4}-\d{2}-\d{2}$/.test(emissao)) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(emissao)!;
  const y = +m[1], mo = +m[2], d = +m[3];
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return '';
  dt.setDate(dt.getDate() + (Number.isFinite(prazo) ? prazo : 0));
  // Formata YYYY-MM-DD sem timezone shift
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

// ─── Caches em módulo (singleton) ─────────────────────────────────────
// TTL curto/longo conforme uso — clientes/taxonomias raramente mudam,
// usuários mudam mais frequentemente.

// Cache simples da lista de usuários do grupo operacional (evita refetch toda
// vez que monta um dropdown). 60s TTL — curto pra que mudanças de cadastro
// CREA em Admin reflitam logo no autofill do wizard de ART.
let _usuariosOpCache: { ts: number; data: UsuarioOp[] } | null = null;
export async function carregarUsuariosOp(): Promise<UsuarioOp[]> {
  if (_usuariosOpCache && Date.now() - _usuariosOpCache.ts < 60 * 1000) {
    return _usuariosOpCache.data;
  }
  try {
    const token = await getToken();
    // [#fix] Filtra por MÓDULO (não por grupo Cognito nativo) — assim perfis
    // customizados como "Diretoria" que ganham acesso ao operacional via
    // permissões em perfil_permissoes também aparecem no dropdown.
    const r = await axios.get(`${API_URL}/admin/usuarios?modulo=operacional`,
      { headers: { Authorization: token } });
    const data = r.data.body ? JSON.parse(r.data.body) : r.data;
    const lista: UsuarioOp[] = (Array.isArray(data) ? data : [])
      .filter((u: any) => u.email && u.enabled !== false)
      .map((u: any) => ({
        email: u.email, nome: u.nome || u.email.split('@')[0],
        crea: u.crea || '', crea_rnp: u.crea_rnp || '', crea_titulo: u.crea_titulo || '',
      }));
    _usuariosOpCache = { ts: Date.now(), data: lista };
    return lista;
  } catch { return []; }
}

// ─── Caches pra ART (clientes + taxonomias CREA) ──────────────────────────
// TTL 10 min — clientes/taxonomias mudam raramente; modal de ART é o único
// consumidor por enquanto, então vale evitar refetch.
// incluirContrapartes=true traz também os pagadores simples importados do
// Conta Azul (tipo_cadastro='contraparte', ex.: Banco Inter) — usado pelo
// Select de pagador do financeiro. Cache separado por variante.
const _clientesArtCache: Record<string, { ts: number; data: ClienteSlim[] } | undefined> = {};
export async function carregarClientesArt(incluirContrapartes = false): Promise<ClienteSlim[]> {
  const chave = incluirContrapartes ? 'com_contrapartes' : 'so_comerciais';
  const cache = _clientesArtCache[chave];
  if (cache && Date.now() - cache.ts < 10 * 60 * 1000) {
    return cache.data;
  }
  try {
    const token = await getToken();
    const qs = incluirContrapartes ? '?incluir_contrapartes=1' : '';
    const r = await axios.get(`${API_URL}/comercial/clientes${qs}`,
      { headers: { Authorization: token } });
    const data = r.data.body ? JSON.parse(r.data.body) : r.data;
    const lista: ClienteSlim[] = (Array.isArray(data) ? data : []).map((c: any) => ({
      id: c.id, razao_social: c.razao_social || `Cliente #${c.id}`,
      cnpj: c.cnpj || null, nome_fantasia: c.nome_fantasia || null,
      tipo_cadastro: c.tipo_cadastro || 'comercial',
    })).sort((a, b) => a.razao_social.localeCompare(b.razao_social));
    _clientesArtCache[chave] = { ts: Date.now(), data: lista };
    return lista;
  } catch { return []; }
}

// Lista contatos vinculados a UM cliente — usado pra trocar contato principal
// do contrato (card Cliente em VisaoGeral). Sem cache (pode mudar quando o
// usuário cria um contato novo via Modal inline). 2026-06-01.
export async function carregarContatosCliente(clienteId: number): Promise<ContatoClienteSlim[]> {
  if (!clienteId) return [];
  try {
    const token = await getToken();
    const r = await axios.get(`${API_URL}/comercial/clientes/${clienteId}/contatos`,
      { headers: { Authorization: token } });
    const data = r.data?.body ? JSON.parse(r.data.body) : r.data;
    return (Array.isArray(data) ? data : []).map((c: any) => ({
      id: c.id, nome: c.nome,
      email: c.email || null, telefone: c.telefone || null,
      cargo: c.cargo || c.cargo_neste || null,
      decisor: !!c.decisor, principal: !!c.principal,
    }));
  } catch { return []; }
}

// Versão "completa" — retorna o array bruto pra autofill de endereço/telefone
// no modal de ART. Reaproveita a mesma resposta /comercial/clientes mas mantém
// todos os campos (endereco, bairro, cep, cidade, uf, contato_fone).
let _clientesFullCache: { ts: number; data: any[] } | null = null;
export async function carregarClientesCompletoCacheOuApi(forceRefresh = false): Promise<any[]> {
  if (!forceRefresh && _clientesFullCache && Date.now() - _clientesFullCache.ts < 10 * 60 * 1000) {
    return _clientesFullCache.data;
  }
  try {
    const token = await getToken();
    const r = await axios.get(`${API_URL}/comercial/clientes`,
      { headers: { Authorization: token } });
    const data = r.data.body ? JSON.parse(r.data.body) : r.data;
    const lista = (Array.isArray(data) ? data : [])
      .filter((c: any) => c.razao_social)
      .sort((a: any, b: any) => a.razao_social.localeCompare(b.razao_social));
    _clientesFullCache = { ts: Date.now(), data: lista };
    return lista;
  } catch { return []; }
}

const _catCache: Record<'tecnica'|'especifica', { ts: number; data: CatAtividadeSlim[] } | null> = {
  tecnica: null, especifica: null,
};
export async function carregarCatAtividades(tipo: 'tecnica'|'especifica'): Promise<CatAtividadeSlim[]> {
  const c = _catCache[tipo];
  if (c && Date.now() - c.ts < 10 * 60 * 1000) return c.data;
  try {
    const token = await getToken();
    const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
      { action: 'op_listar_cat_atividades', tipo },
      { headers: { Authorization: token } });
    const data = r.data.body ? JSON.parse(r.data.body) : r.data;
    const lista: CatAtividadeSlim[] = (Array.isArray(data) ? data : [])
      .map((x: any) => ({ id: x.id, nome: x.nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome));
    _catCache[tipo] = { ts: Date.now(), data: lista };
    return lista;
  } catch { return []; }
}

// Caches de dropdowns CREA-RS (Onda 4)
let _opcoesArtCache: { ts: number; data: OpcoesArt } | null = null;
export async function carregarOpcoesArt(): Promise<OpcoesArt> {
  if (_opcoesArtCache && Date.now() - _opcoesArtCache.ts < 30 * 60 * 1000) {
    return _opcoesArtCache.data;
  }
  try {
    const token = await getToken();
    const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
      { action: 'op_listar_opcoes_art' },
      { headers: { Authorization: token } });
    const d = r.data.body ? JSON.parse(r.data.body) : r.data;
    _opcoesArtCache = { ts: Date.now(), data: d };
    return d;
  } catch {
    return {
      tipos_art: ['Obra ou Serviço'], convenios: ['Não é convênio'],
      participacoes_tecnicas: ['Individual/Principal'], motivos: ['Normal'],
      finalidades: ['Outras Finalidades'],
      status_crea: [{ codigo: 'rascunho_interno', label: 'Rascunho interno' }],
      empresa_principal: null,
    };
  }
}

let _unidadesCreaCache: { ts: number; data: Array<{ sigla: string; nome: string }> } | null = null;
export async function carregarUnidadesCrea(): Promise<Array<{ sigla: string; nome: string }>> {
  if (_unidadesCreaCache && Date.now() - _unidadesCreaCache.ts < 30 * 60 * 1000) {
    return _unidadesCreaCache.data;
  }
  try {
    const token = await getToken();
    const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
      { action: 'op_listar_unidades_crea' },
      { headers: { Authorization: token } });
    const d = r.data.body ? JSON.parse(r.data.body) : r.data;
    _unidadesCreaCache = { ts: Date.now(), data: d };
    return d;
  } catch { return [{ sigla: 'UN', nome: 'Unidade' }]; }
}

let _tiposLogradouroCache: { ts: number; data: string[] } | null = null;
export async function carregarTiposLogradouro(): Promise<string[]> {
  if (_tiposLogradouroCache && Date.now() - _tiposLogradouroCache.ts < 30 * 60 * 1000) {
    return _tiposLogradouroCache.data;
  }
  try {
    const token = await getToken();
    const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
      { action: 'op_listar_tipos_logradouro' },
      { headers: { Authorization: token } });
    const d = r.data.body ? JSON.parse(r.data.body) : r.data;
    _tiposLogradouroCache = { ts: Date.now(), data: d };
    return d;
  } catch { return ['OUTROS']; }
}

// Busca CEP via backend (proxy ViaCEP)
export async function buscarCep(cep: string): Promise<{
  cep: string; logradouro: string; complemento: string;
  bairro: string; municipio: string; uf: string;
} | null> {
  try {
    const token = await getToken();
    const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
      { action: 'op_buscar_cep', cep },
      { headers: { Authorization: token } });
    const d = r.data.body ? JSON.parse(r.data.body) : r.data;
    if (d?.error) return null;
    return d;
  } catch { return null; }
}
